import { BRAND } from "@shared/branding";
import React, { useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";

import PhoneNumberField from "@components/Form/PhoneNumberField";

import httpService from "@shared/services/http.service";
import { API_CONFIG, User_Role } from "@shared/constants/api";
import { SignUpFormValues, signupSchema } from "@shared/validations/signup.schema";
import { notify } from "@/shared/tostNotification/notify";
import { registerUser } from "@/store/slices/authSlice";
import { useAppDispatch } from "@/store/hooks";
import { useSelector } from "react-redux";
import { selectAuthLoading } from "@/store/selectors/authSelectors";

// Types & Schemas

const SignUpForm: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // const [isLoading, setIsLoading] = useState(false);

  const isLoading = useSelector(selectAuthLoading);

  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SignUpFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      rememberMe: false,
    },
    mode: "onTouched",
  });

  const onSubmit = useCallback(
    async (data: SignUpFormValues) => {
      if (!data.rememberMe) {
        notify("You must agree to the terms and conditions", "error");
        return;
      }

      const body = {
        fullName: data.fullName,
        email: data.email,
        mobile: data.phone,
        password: data.password,
        role: User_Role.agent,
        isAgree: data.rememberMe,
      };

      const resultAction = await dispatch(registerUser(body));

      if (registerUser.fulfilled.match(resultAction)) {
        navigate("/login");
        return;
      }
    },
    [dispatch, navigate]
  );

  return (
    <div className="flex size-full items-center justify-center p-4 sm:px-6 md:w-1/2 md:px-8 ">
      <div className="size-full max-w-xs rounded-2xl border border-gray-200 bg-white py-5 pl-4 shadow-sm sm:max-w-sm sm:rounded-3xl sm:py-6 sm:pl-6 ">
        {/* Logo for Mobile */}
        <div className="mb-4 block md:hidden">
          <img src="/goodweek_auth_mobile.svg" alt="Goodweeks Logo" width={150} height={50} />
        </div>

        {/* Heading */}
        <h1 className="mb-4 text-base font-bold text-gray-900 sm:mb-6 sm:text-xl md:text-xl">
          Become {BRAND.name ? `a ${BRAND.name}` : "an"} Agent
        </h1>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex h-[calc(100%-50px)] flex-col pl-[2px]  pr-4"
          noValidate
        >
          {/* Scrollable form fields */}
          <div className="max-h-[55vh] flex-grow overflow-y-auto pl-2 pr-4 sm:max-h-[60vh] md:max-h-full">
            {/* Full Name Field */}
            <div className="mb-4 sm:mb-3">
              <label className="mb-2 block text-xs font-medium text-gray-700 sm:text-sm">
                Full Name
              </label>
              <input
                type="text"
                autoComplete="name"
                className={`h-9 w-full rounded-lg border px-3 py-2 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-orange-600 sm:h-10 sm:text-sm ${errors.fullName ? "border-red-300" : "border-gray-300"
                  } ${isLoading ? "bg-gray-50" : ""}`}
                placeholder="Enter your full name"
                disabled={isLoading}
                {...register("fullName")}
              />
              {errors.fullName && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div className="mb-4 sm:mb-3">
              <label className="mb-2 block text-xs font-medium text-gray-700 sm:text-sm">
                Email
              </label>
              <input
                type="email"
                autoComplete="email"
                className={`h-9 w-full rounded-lg border px-3 py-2 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-orange-600 sm:h-10 sm:text-sm ${errors.email ? "border-red-300" : "border-gray-300"
                  } ${isLoading ? "bg-gray-50" : ""}`}
                placeholder="Enter your email"
                disabled={isLoading}
                {...register("email")}
              />
              {errors.email && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone Field */}
            <div className="mb-4 sm:mb-3">
              <Controller
                control={control}
                name="phone"
                render={({ field: phoneField }) => (
                  <PhoneNumberField
                    label="Phone Number"
                    required
                    value={phoneField.value}
                    onChange={phoneField.onChange}
                    onBlur={phoneField.onBlur}
                    placeholder="Enter your phone number (10 digits)"
                    error={errors.phone?.message}
                    disabled={isLoading}
                    inputClassName="!h-9 !border-[0.5px] !border-[#999] dark:!border-[#595959] !bg-white dark:!bg-[#1f1f1f] !py-2 !text-sm !text-black dark:!text-white"
                    buttonClassName="!border-[0.5px] !border-[#999] dark:!border-[#595959] !bg-white dark:!bg-transparent !rounded-l-[8px] focus:!outline-none focus:!ring-1 focus:!ring-orange-500 focus:!border-orange-500"
                    inputProps={{
                      name: phoneField.name,
                      autoComplete: "tel",
                    }}
                  />
                )}
              />
            </div>

            {/* Password Field */}
            <div className="mb-4 sm:mb-3">
              <label className="mb-2 block text-xs font-medium text-gray-700 sm:text-sm">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  className={`h-9 w-full rounded-lg border px-3 py-2 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-orange-600 sm:h-10 sm:text-sm ${errors.password ? "border-red-300" : "border-gray-300"
                    } ${isLoading ? "bg-gray-50" : ""}`}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  {...register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 transform p-1 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff size={18} aria-hidden="true" />
                  ) : (
                    <Eye size={18} aria-hidden="true" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="mb-4 sm:mb-3">
              <label className="mb-2 block text-xs font-medium text-gray-700 sm:text-sm">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  className={`h-9 w-full rounded-lg border px-3 py-2 text-xs focus:border-transparent focus:outline-none focus:ring-2 focus:ring-orange-600 sm:h-10 sm:text-sm ${errors.confirmPassword ? "border-red-300" : "border-gray-300"
                    } ${isLoading ? "bg-gray-50" : ""}`}
                  placeholder="Confirm your password"
                  disabled={isLoading}
                  {...register("confirmPassword")}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 transform p-1 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} aria-hidden="true" />
                  ) : (
                    <Eye size={18} aria-hidden="true" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
                  <span className="text-red-500">⚠</span>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {/* Terms Checkbox */}
            <div className="mb-6 flex items-center">
              <div className="flex h-5 items-center">
                <input
                  type="checkbox"
                  id="terms"
                  disabled={isLoading}
                  className={`size-4 rounded focus:ring-2 focus:ring-orange-600 ${errors.rememberMe
                    ? "border-red-300 text-red-600"
                    : "border-gray-300 text-blue-600"
                    }`}
                  {...register("rememberMe")}
                />
              </div>
              <label htmlFor="terms" className="ml-2 text-[10px] text-gray-600 sm:text-xs">
                I agree to the{" "}
                <a href="#" className="font-medium text-blue-600 hover:underline">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="#" className="font-medium text-blue-600 hover:underline">
                  Privacy Policy
                </a>
                {errors.rememberMe && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
                    <span className="text-red-500">⚠</span>
                    {errors.rememberMe.message}
                  </p>
                )}
              </label>
            </div>
          </div>

          {/* Fixed bottom section */}
          <div className="border-t border-gray-100 pt-4">
            {/* Create Account Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-900 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-blue-950 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-900 sm:py-3 sm:text-base"
            >
              {isLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin sm:size-5" />
                  <span>Creating Account...</span>
                </>
              ) : (
                "Create Account"
              )}
            </button>

            {/* Sign In Link */}
            <div className="text-center">
              <span className="text-xs font-normal text-gray-600 sm:text-sm">
                Already have an account?{" "}
              </span>
              <button
                type="button"
                onClick={() => navigate("/login")}
                disabled={isLoading}
                className="cursor-pointer rounded text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
              >
                Sign In
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignUpForm;
